<template>
  <app-layout>
    <div class="sc-container msg-page">

      <!-- Header -->
      <div class="msg-header">
        <h1 class="msg-title">Messages</h1>
        <button class="msg-btn msg-btn--primary" @click="showComposeModal = true">
          <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Message
        </button>
      </div>

      <!-- Two-panel -->
      <div class="msg-panel">

        <!-- Sidebar -->
        <div class="msg-sidebar" :class="{ 'msg-sidebar--hidden': mobileShowChat }">
          <!-- Search -->
          <div class="msg-search">
            <svg class="msg-search__icon" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              v-model="tableData.search"
              @input="fetchUsers"
              type="text"
              placeholder="Search people…"
              class="msg-search__input"
            />
          </div>

          <!-- Thread list -->
          <div class="msg-list">
            <!-- Skeleton loader -->
            <template v-if="initialLoading">
              <div v-for="n in 6" :key="'sk-' + n" class="msg-thread-skeleton">
                <div class="msg-sk-avatar"></div>
                <div class="msg-sk-body">
                  <div class="msg-sk-line msg-sk-line--name"></div>
                  <div class="msg-sk-line msg-sk-line--preview"></div>
                </div>
              </div>
            </template>

            <!-- Search results -->
            <template v-else-if="tableData.search">
              <button
                v-for="u in search_result"
                :key="u.id"
                class="msg-thread"
                @click="messageUser(u.id)"
              >
                <img
                  :src="u.avatar ? $avatarSrc(u.avatar, 'users') : initAvatar(u.name)"
                  class="msg-thread__avatar"
                  :alt="u.name"
                  @error="e => e.target.src = initAvatar(u.name)"
                />
                <div class="msg-thread__body">
                  <span class="msg-thread__name">{{ u.name }}</span>
                  <span class="msg-thread__preview">Start a conversation</span>
                </div>
              </button>
            </template>

            <!-- Conversations -->
            <template v-else-if="!initialLoading">
              <button
                v-for="(data, index) in messenger"
                :key="data.id"
                class="msg-thread"
                :class="{
                  'msg-thread--active': current_index === index,
                  'msg-thread--unread': data.status == 0
                }"
                @click="viewDetail(index, data.id)"
              >
                <div class="msg-thread__avatar-wrap">
                  <img
                    v-if="id == data.user_id && data.sender"
                    :src="data.sender.avatar ? $avatarSrc(data.sender.avatar, 'users') : initAvatar(data.sender.name)"
                    class="msg-thread__avatar"
                    :alt="data.sender.name"
                    @error="e => e.target.src = initAvatar(data.sender.name)"
                  />
                  <img
                    v-else-if="data.user"
                    :src="data.user.avatar ? $avatarSrc(data.user.avatar, 'users') : initAvatar(data.user.name)"
                    class="msg-thread__avatar"
                    :alt="data.user.name"
                    @error="e => e.target.src = initAvatar(data.user.name)"
                  />
                  <span v-if="data.status == 0" class="msg-thread__unread-dot"></span>
                </div>
                <div class="msg-thread__body">
                  <div class="msg-thread__top">
                    <span class="msg-thread__name">
                      {{ id == data.user_id ? (data.sender ? data.sender.name : '') : (data.user ? data.user.name : '') }}
                    </span>
                    <span v-if="data.lastdetails && data.lastdetails.length" class="msg-thread__time">
                      {{ formatDate(data.lastdetails[0].created_at) }}
                    </span>
                  </div>
                  <span v-if="data.details && data.details.length" class="msg-thread__preview">
                    {{ data.details[0].message }}
                  </span>
                </div>
              </button>

              <div v-if="!messenger.length" class="msg-list-empty">
                <svg width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                <p>No messages yet</p>
              </div>
            </template>
          </div>
        </div>

        <!-- Chat pane -->
        <div class="msg-chat" :class="{ 'msg-chat--visible': mobileShowChat }">

          <!-- No selection -->
          <div v-if="!currentData" class="msg-empty">
            <svg width="56" height="56" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            <p>Select a conversation to start messaging</p>
          </div>

          <template v-else>
            <!-- Chat header -->
            <div class="msg-chat-header">
              <button class="msg-back-btn" @click="mobileShowChat = false">
                <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <div class="msg-chat-header__info">
                <span class="msg-chat-header__name">
                  {{ id == currentData.user_id ? (currentData.sender ? currentData.sender.name : '') : (currentData.user ? currentData.user.name : '') }}
                </span>
                <span class="msg-chat-header__role">
                  {{ id == currentData.user_id ? (currentData.sender ? currentData.sender.profession : '') : (currentData.user ? currentData.user.profession : '') }}
                </span>
              </div>
              <div class="msg-chat-header__actions">
                <a
                  v-if="id == currentData.user_id && currentData.sender && currentData.sender.phone_number"
                  :href="'tel:' + currentData.sender.phone_number"
                  class="msg-icon-btn"
                  title="Call"
                >
                  <svg width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 013.07 4.18 2 2 0 015.06 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L9.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                </a>
                <a
                  v-else-if="id != currentData.user_id && currentData.user && currentData.user.phone_number"
                  :href="'tel:' + currentData.user.phone_number"
                  class="msg-icon-btn"
                  title="Call"
                >
                  <svg width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 013.07 4.18 2 2 0 015.06 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L9.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                </a>

                <div class="msg-menu-wrap">
                  <button class="msg-icon-btn" @click="showChatMenu = !showChatMenu">
                    <svg width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
                  </button>
                  <div v-if="showChatMenu" class="msg-dropdown" v-click-outside="() => showChatMenu = false">
                    <button class="msg-dropdown__item" @click="deleteMessage(currentData.id); showChatMenu = false">
                      <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                      Delete conversation
                    </button>
                    <button class="msg-dropdown__item" @click="clearMessengerChat(currentData.id); showChatMenu = false">
                      <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                      Clear chat
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Messages -->
            <div class="msg-chat-body" ref="chatBody">
              <div
                v-for="(detail, idx) in currentData.details"
                :key="idx"
                class="msg-bubble-wrap"
                :class="{ 'msg-bubble-wrap--mine': String(detail.sender_id) === String(id) }"
              >
                <img
                  v-if="detail.sender"
                  :src="detail.sender.avatar ? $avatarSrc(detail.sender.avatar, 'users') : initAvatar(detail.sender.name)"
                  class="msg-bubble-avatar"
                  :alt="detail.sender ? detail.sender.name : ''"
                  @error="e => e.target.src = initAvatar(detail.sender ? detail.sender.name : '')"
                />
                <div class="msg-bubble" :class="{ 'msg-bubble--mine': String(detail.sender_id) === String(id) }">
                  <p>{{ detail.message }}</p>
                  <span class="msg-bubble-time">{{ formatDate(detail.created_at) }}</span>
                </div>
              </div>

              <div v-if="!currentData.details || !currentData.details.length" class="msg-no-messages">
                No messages yet. Say hello!
              </div>
            </div>

            <!-- Compose -->
            <div class="msg-compose">
              <textarea
                v-model="messageText"
                class="msg-compose__input"
                placeholder="Write a message…"
                rows="1"
                @keydown.enter.exact.prevent="sendMessage"
                @keydown.enter.shift.exact="messageText += '\n'"
              ></textarea>
              <button class="msg-compose__send" :disabled="loading || !messageText.trim()" @click="sendMessage">
                <svg v-if="!loading" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                <svg v-else width="18" height="18" class="msg-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" opacity=".25"/><path d="M12 3a9 9 0 019 9" stroke-linecap="round"/></svg>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Compose modal -->
    <transition name="msg-fade">
      <div v-if="showComposeModal" class="msg-modal-backdrop" @click.self="showComposeModal = false">
        <div class="msg-modal">
          <div class="msg-modal__header">
            <h3 class="msg-modal__title">New Message</h3>
            <button class="msg-modal__close" @click="showComposeModal = false">
              <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="msg-modal__body">
            <div class="msg-field">
              <label class="msg-label">To</label>
              <multiselect
                v-model="message.user_id"
                label="name"
                placeholder="Search people…"
                :options="users"
                :allow-empty="true"
              />
            </div>
            <div class="msg-field">
              <label class="msg-label">Message</label>
              <textarea
                v-model="message.message"
                class="msg-modal__textarea"
                rows="5"
                placeholder="Type your message…"
              ></textarea>
            </div>
          </div>
          <div class="msg-modal__footer">
            <button class="msg-btn msg-btn--outline" @click="showComposeModal = false">Cancel</button>
            <button class="msg-btn msg-btn--primary" :disabled="loading" @click="composeMessage">
              <span v-if="loading">Sending…</span>
              <span v-else>Send Message</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout';
import axios from 'axios';
import moment from 'moment';
import Multiselect from 'vue-multiselect';

export default {
  name: 'Messages',
  components: { AppLayout, Multiselect },

  data() {
    return {
      company: {},
      currentData: '',
      current_index: '',
      user: {},
      messenger: [],
      loading: false,
      initialLoading: true,
      id: localStorage.getItem('user'),
      search_result: [],
      tableData: { draw: 0, search: '' },
      users: [],
      message: { user_id: '', message: '', sender_id: localStorage.getItem('user') },
      messageText: '',
      showComposeModal: false,
      showChatMenu: false,
      mobileShowChat: false,
    };
  },

  computed: {
    imageUrl() {
      return process.env.VUE_APP_IMAGE_URL || '';
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        document.title = to.meta.title || 'Messages';
      },
    },
  },

  created() {
    const withUserId = this.$route.query.with;
    if (withUserId) {
      this.messageUser(withUserId);
    } else {
      this.fetchData();
    }
    this.fetchUser();
    this.fetchAllUsers();
  },

  mounted() {
    this.onFireAction = () => this.fetchData();
    window.Fire.on('action', this.onFireAction);
  },

  beforeUnmount() {
    window.Fire.off('action', this.onFireAction);
  },

  methods: {
    fetchData(callback) {
      return axios(process.env.VUE_APP_API_URL + 'messenger/fetch/' + this.id)
        .then(res => {
          if (res.status !== 200) return;
          this.messenger = res.data;
          if (this.current_index !== '') {
            this.currentData = this.messenger[this.current_index] || this.currentData;
            this.$nextTick(() => { if (this.$refs.chatBody) this.$refs.chatBody.scrollTop = 99999; });
          }
          if (callback) callback();
        })
        .catch(() => {})
        .finally(() => { this.initialLoading = false; });
    },

    fetchUsers() {
      if (!this.tableData.search) { this.search_result = []; return; }
      this.tableData.draw++;
      axios(process.env.VUE_APP_API_URL + 'messenger/fetch-users/' + this.id, { params: this.tableData })
        .then(res => { if (res.status === 200) this.search_result = res.data; })
        .catch(() => {});
    },

    fetchAllUsers() {
      axios(process.env.VUE_APP_API_URL + 'users')
        .then(res => { if (res.status === 200) this.users = res.data; })
        .catch(() => {});
    },

    fetchUser() {
      axios(process.env.VUE_APP_API_URL + 'user/details/' + this.id)
        .then(res => { if (res.status === 200) this.user = res.data; })
        .catch(() => {});
    },

    viewDetail(index, id) {
      axios(process.env.VUE_APP_API_URL + 'messenger/update/' + id).catch(() => {});
      this.current_index = index;
      this.currentData = this.messenger[index];
      this.mobileShowChat = true;
      this.$nextTick(() => { if (this.$refs.chatBody) this.$refs.chatBody.scrollTop = 99999; });
    },

    sendMessage() {
      if (!this.messageText.trim() || !this.currentData) return;
      const recipientId = String(this.id) === String(this.currentData.user_id)
        ? this.currentData.sender_id
        : this.currentData.user_id;
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'messenger/send', {
        sender_id: this.id,
        user_id: recipientId,
        message: this.messageText,
      })
        .then(res => {
          if (res.status === 200) {
            this.messageText = '';
            window.Fire.emit('action');
          } else {
            this.$toastr.e(res.message);
          }
        })
        .catch(err => { this.$toastr.e(err?.response?.data?.message || 'Failed to send'); })
        .finally(() => { this.loading = false; });
    },

    composeMessage() {
      if (!this.message.user_id || !this.message.message) return;
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'messenger/compose-new', this.message)
        .then(res => {
          if (res.status === 200) {
            this.showComposeModal = false;
            window.Fire.emit('action');
            this.$toastr.s(res.message || 'Message sent');
            this.message = { user_id: '', message: '', sender_id: this.id };
          } else {
            this.$toastr.e(res.message);
          }
        })
        .catch(err => { this.$toastr.e(err?.response?.data?.message || 'Failed to send'); })
        .finally(() => { this.loading = false; });
    },

    messageUser(userId) {
      axios(process.env.VUE_APP_API_URL + 'messenger/initiate/' + userId + '/' + this.id)
        .then(res => {
          // 200 = new conversation created, 201 = already exists — both mean proceed
          if (res.status !== 200 && res.status !== 201) return;
          this.tableData.search = '';
          this.search_result = [];
          this.fetchData(() => {
            const idx = this.messenger.findIndex(m =>
              String(m.user_id) === String(userId) || String(m.sender_id) === String(userId)
            );
            const target = idx !== -1 ? idx : 0;
            if (this.messenger[target]) this.viewDetail(target, this.messenger[target].id);
          });
        })
        .catch(() => {});
    },

    deleteMessage(id) {
      axios(process.env.VUE_APP_API_URL + 'messenger/delete/' + id)
        .then(res => { this.$toastr.s(res.message || 'Deleted'); this.currentData = ''; this.fetchData(); })
        .catch(() => {});
    },

    clearMessengerChat(id) {
      axios(process.env.VUE_APP_API_URL + 'messenger/clear-chat/' + id)
        .then(res => { this.$toastr.s(res.message || 'Chat cleared'); this.fetchData(); })
        .catch(() => {});
    },

    formatDate(date) {
      return moment(date).fromNow();
    },

    nameColor(name) {
      const palette = ['#4f46e5','#0891b2','#059669','#d97706','#7c3aed','#db2777','#ea580c','#0284c7'];
      let h = 0;
      for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
      return palette[Math.abs(h) % palette.length];
    },

    initAvatar(name, size = 36) {
      const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
      const bg = this.nameColor(name);
      const fs = Math.round(size * 0.38);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="${bg}"/><text x="${size/2}" y="${size*0.64}" font-family="Arial,sans-serif" font-size="${fs}" font-weight="700" fill="#fff" text-anchor="middle">${initials}</text></svg>`;
      return 'data:image/svg+xml;base64,' + btoa(svg);
    },
  },

  directives: {
    clickOutside: {
      mounted(el, binding) {
        el.__clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el.__clickOutside);
      },
      unmounted(el) {
        document.removeEventListener('click', el.__clickOutside);
        delete el.__clickOutside;
      },
    },
  },
};
</script>

<style scoped>
/* ── Page ── */
.msg-page { padding: 32px 16px 0; }

.msg-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 20px; flex-wrap: wrap; gap: 12px;
}
.msg-title { font-size: 1.5rem; font-weight: 700; color: var(--text, #111827); margin: 0; }

/* ── Buttons ── */
.msg-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; border-radius: 8px; font-size: .875rem;
  font-weight: 600; cursor: pointer; border: none; font-family: inherit;
  transition: all .18s; text-decoration: none;
}
.msg-btn--primary { background: var(--primary, #1e3a5f); color: #fff; }
.msg-btn--primary:hover { filter: brightness(1.1); }
.msg-btn--primary:disabled { opacity: .6; cursor: not-allowed; }
.msg-btn--outline {
  background: none; border: 1.5px solid var(--border, #e5e7eb);
  color: var(--text, #111827);
}
.msg-btn--outline:hover { border-color: var(--primary, #1e3a5f); color: var(--primary, #1e3a5f); }

/* ── Panel ── */
.msg-panel {
  display: flex;
  height: calc(100vh - 180px);
  min-height: 500px;
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--radius, 10px);
  overflow: hidden;
  background: var(--surface, #fff);
  box-shadow: var(--shadow, 0 2px 8px rgba(0,0,0,.08));
}

/* ── Sidebar ── */
.msg-sidebar {
  width: 320px;
  flex-shrink: 0;
  border-right: 1px solid var(--border-light, #e5e7eb);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.msg-search {
  position: relative;
  padding: 12px;
  border-bottom: 1px solid var(--border-light, #e5e7eb);
  flex-shrink: 0;
}
.msg-search__icon {
  position: absolute; left: 22px; top: 50%; transform: translateY(-50%);
  color: var(--text-muted, #6b7280); pointer-events: none;
}
.msg-search__input {
  width: 100%; padding: 8px 12px 8px 36px;
  border: 1px solid var(--border-light, #e5e7eb); border-radius: 8px;
  font-size: .875rem; background: var(--surface-3, #f3f4f6);
  color: var(--text, #111827); outline: none;
  transition: border-color .15s;
}
.msg-search__input:focus { border-color: var(--primary, #1e3a5f); background: var(--surface, #fff); }

.msg-list { flex: 1; overflow-y: auto; }
.msg-list::-webkit-scrollbar { width: 4px; }
.msg-list::-webkit-scrollbar-thumb { background: var(--border, #e5e7eb); border-radius: 2px; }

.msg-thread {
  width: 100%; display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; border: none; background: none; cursor: pointer;
  border-bottom: 1px solid var(--border-light, #e5e7eb);
  text-align: left; font-family: inherit;
  transition: background .15s;
}
.msg-thread:hover { background: var(--surface-3, #f3f4f6); }
.msg-thread--active { background: rgba(30,58,95,.06); }
.msg-thread--active:hover { background: rgba(30,58,95,.1); }

.msg-thread__avatar-wrap { position: relative; flex-shrink: 0; }
.msg-thread__avatar {
  width: 40px; height: 40px; border-radius: 50%; object-fit: cover;
  display: block; flex-shrink: 0;
}
.msg-thread__unread-dot {
  position: absolute; bottom: 1px; right: 1px;
  width: 10px; height: 10px; border-radius: 50%;
  background: var(--primary, #1e3a5f); border: 2px solid var(--surface, #fff);
}
.msg-thread__body { flex: 1; min-width: 0; }
.msg-thread__top { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; }
.msg-thread__name {
  font-size: .875rem; font-weight: 500; color: var(--text, #111827);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.msg-thread--unread .msg-thread__name { font-weight: 700; }
.msg-thread__time { font-size: .72rem; color: var(--text-muted, #6b7280); white-space: nowrap; flex-shrink: 0; }
.msg-thread__preview {
  display: block; font-size: .78rem; color: var(--text-muted, #6b7280);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px;
}

.msg-thread-skeleton {
  display: flex; align-items: center; gap: .875rem;
  padding: .875rem 1rem; border-bottom: 1px solid var(--border-light);
}
.msg-sk-avatar {
  width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%; animation: msg-shimmer 1.4s infinite;
}
.msg-sk-body { flex: 1; display: flex; flex-direction: column; gap: .5rem; }
.msg-sk-line {
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%; animation: msg-shimmer 1.4s infinite;
}
.msg-sk-line--name { height: 13px; width: 55%; }
.msg-sk-line--preview { height: 11px; width: 80%; }
@keyframes msg-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.msg-list-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px 16px; color: var(--text-muted, #9ca3af); gap: 10px; text-align: center;
}
.msg-list-empty p { font-size: .875rem; margin: 0; }

/* ── Chat pane ── */
.msg-chat {
  flex: 1; display: flex; flex-direction: column; min-width: 0; overflow: hidden;
}

.msg-empty {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  color: var(--text-muted, #9ca3af); gap: 12px; text-align: center; padding: 2rem;
}
.msg-empty p { font-size: .9375rem; margin: 0; }

/* Chat header */
.msg-chat-header {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px; border-bottom: 1px solid var(--border-light, #e5e7eb);
  flex-shrink: 0;
}
.msg-back-btn {
  display: none; border: none; background: none; cursor: pointer;
  color: var(--text-muted, #6b7280); padding: 4px; border-radius: 6px;
}
.msg-back-btn:hover { background: var(--surface-3, #f3f4f6); color: var(--text, #111827); }
.msg-chat-header__info { flex: 1; min-width: 0; }
.msg-chat-header__name { display: block; font-size: .9375rem; font-weight: 700; color: var(--text, #111827); }
.msg-chat-header__role { font-size: .8rem; color: var(--text-muted, #6b7280); }
.msg-chat-header__actions { display: flex; align-items: center; gap: 6px; }
.msg-icon-btn {
  width: 34px; height: 34px; border-radius: 8px; border: none; background: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: var(--text-muted, #6b7280); text-decoration: none;
  transition: background .15s, color .15s;
}
.msg-icon-btn:hover { background: var(--surface-3, #f3f4f6); color: var(--text, #111827); }

/* Dropdown */
.msg-menu-wrap { position: relative; }
.msg-dropdown {
  position: absolute; right: 0; top: calc(100% + 6px); z-index: 100;
  background: var(--surface, #fff);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.12);
  min-width: 180px; overflow: hidden;
}
.msg-dropdown__item {
  width: 100%; display: flex; align-items: center; gap: 8px;
  padding: 9px 14px; border: none; background: none; cursor: pointer;
  font-size: .875rem; color: var(--text, #111827); text-align: left;
  font-family: inherit; transition: background .12s;
}
.msg-dropdown__item:hover { background: var(--surface-3, #f3f4f6); }

/* Chat body */
.msg-chat-body {
  flex: 1; overflow-y: auto; padding: 16px;
  display: flex; flex-direction: column; gap: 12px;
  background: var(--surface-3, #f3f4f6);
}
.msg-chat-body::-webkit-scrollbar { width: 4px; }
.msg-chat-body::-webkit-scrollbar-thumb { background: var(--border, #e5e7eb); border-radius: 2px; }

.msg-no-messages {
  text-align: center; color: var(--text-muted, #9ca3af);
  font-size: .875rem; padding: 32px 0;
}

.msg-bubble-wrap {
  display: flex; align-items: flex-end; gap: 8px;
}
.msg-bubble-wrap--mine { flex-direction: row-reverse; }

.msg-bubble-avatar {
  width: 32px; height: 32px; border-radius: 50%; object-fit: cover; flex-shrink: 0;
}

.msg-bubble {
  max-width: 68%; padding: 10px 14px;
  border-radius: 16px 16px 16px 4px;
  background: var(--surface, #fff);
  box-shadow: 0 1px 3px rgba(0,0,0,.07);
}
.msg-bubble--mine {
  background: var(--primary, #1e3a5f); color: #fff;
  border-radius: 16px 16px 4px 16px;
}
.msg-bubble p { margin: 0 0 4px; font-size: .875rem; line-height: 1.5; white-space: pre-wrap; }
.msg-bubble--mine p { color: #fff; }
.msg-bubble-time { font-size: .7rem; color: var(--text-muted, #9ca3af); display: block; }
.msg-bubble--mine .msg-bubble-time { color: rgba(255,255,255,.65); }

/* Compose bar */
.msg-compose {
  display: flex; align-items: flex-end; gap: 8px;
  padding: 12px 14px; border-top: 1px solid var(--border-light, #e5e7eb);
  background: var(--surface, #fff); flex-shrink: 0;
}
.msg-compose__input {
  flex: 1; resize: none; border: 1px solid var(--border-light, #e5e7eb);
  border-radius: 10px; padding: 10px 14px; font-size: .875rem;
  font-family: inherit; color: var(--text, #111827);
  background: var(--surface-3, #f3f4f6); outline: none;
  max-height: 120px; overflow-y: auto;
  transition: border-color .15s;
  line-height: 1.5;
}
.msg-compose__input:focus { border-color: var(--primary, #1e3a5f); background: var(--surface, #fff); }
.msg-compose__send {
  width: 40px; height: 40px; border-radius: 10px; border: none;
  background: var(--primary, #1e3a5f); color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  transition: filter .15s;
}
.msg-compose__send:hover:not(:disabled) { filter: brightness(1.15); }
.msg-compose__send:disabled { opacity: .5; cursor: not-allowed; }

@keyframes msg-spin { to { transform: rotate(360deg); } }
.msg-spin { animation: msg-spin .8s linear infinite; }

/* ── Compose modal ── */
.msg-modal-backdrop {
  position: fixed; inset: 0; z-index: 800;
  background: rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center; padding: 1.5rem;
}
.msg-modal {
  background: var(--surface, #fff); border-radius: var(--radius, 10px);
  box-shadow: 0 24px 64px rgba(0,0,0,.18);
  width: 100%; max-width: 480px;
}
.msg-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.125rem 1.25rem; border-bottom: 1px solid var(--border-light, #e5e7eb);
}
.msg-modal__title { font-size: 1rem; font-weight: 700; color: var(--text, #111827); margin: 0; }
.msg-modal__close {
  width: 30px; height: 30px; border-radius: 50%; border: none; background: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: var(--text-muted, #6b7280); transition: background .15s;
}
.msg-modal__close:hover { background: var(--surface-3, #f3f4f6); }
.msg-modal__body { padding: 1.25rem; display: flex; flex-direction: column; gap: 16px; }
.msg-modal__footer {
  padding: 1rem 1.25rem; border-top: 1px solid var(--border-light, #e5e7eb);
  display: flex; justify-content: flex-end; gap: 8px;
}
.msg-modal__textarea {
  width: 100%; border: 1px solid var(--border-light, #e5e7eb);
  border-radius: 8px; padding: 10px 12px; font-size: .875rem;
  font-family: inherit; color: var(--text, #111827);
  background: var(--surface-3, #f3f4f6); outline: none; resize: vertical;
  transition: border-color .15s;
}
.msg-modal__textarea:focus { border-color: var(--primary, #1e3a5f); background: var(--surface, #fff); }
.msg-label { display: block; font-size: .8125rem; font-weight: 600; color: var(--text, #111827); margin-bottom: 6px; }
.msg-field { display: flex; flex-direction: column; }

/* ── Transition ── */
.msg-fade-enter-active, .msg-fade-leave-active { transition: opacity .2s; }
.msg-fade-enter-from, .msg-fade-leave-to { opacity: 0; }

/* ── Mobile ── */
@media (max-width: 700px) {
  .msg-page { padding: 16px 12px 0; }
  .msg-panel { height: calc(100vh - 140px); }

  .msg-sidebar {
    width: 100%;
    position: absolute;
    inset: 0;
    z-index: 1;
    border-right: none;
    border-radius: var(--radius, 10px);
    background: var(--surface, #fff);
  }
  .msg-sidebar--hidden { display: none; }

  .msg-chat {
    width: 100%;
    position: absolute;
    inset: 0;
    display: none;
  }
  .msg-chat--visible { display: flex; }

  .msg-panel { position: relative; }
  .msg-back-btn { display: flex; }
}
</style>
